import type {
  AgentStatus,
  CadenceAgent,
  CadenceCollaborationInteraction,
  CollaborationAgentState,
  CadenceDelegatedAgent,
} from "./types";

type UnknownRecord = Record<string, unknown>;

export interface OrganizationAgentEventRecord {
  key: string;
  seq: number;
  tsMs?: number;
  eventId?: string;
  source?: string;
  sourceIndex?: number;
  sourceWatermark?: string | number;
  organizationSessionId?: string;
  projectId: string;
  agentId: string;
  threadId: string;
  submissionId: string;
  event: UnknownRecord & { type: OrganizationAgentEventType };
}

type KnownOrganizationAgentEventType =
  | "task_started"
  | "task_complete"
  | "turn_started"
  | "turn_complete"
  | "turn_aborted"
  | "error"
  | "agent_message"
  | "agent_message_delta"
  | "agent_message_content_delta"
  | "agent_reasoning"
  | "agent_reasoning_delta"
  | "reasoning_content_delta"
  | "mcp_tool_call_begin"
  | "mcp_tool_call_end"
  | "web_search_begin"
  | "web_search_end"
  | "exec_command_begin"
  | "exec_command_output_delta"
  | "exec_command_end"
  | "plan_update"
  | "plan_delta"
  | "item_started"
  | "item_completed"
  | "stream_error"
  | "collab_agent_spawn_begin"
  | "collab_agent_spawn_end"
  | "collab_agent_interaction_begin"
  | "collab_agent_interaction_end"
  | "collab_waiting_begin"
  | "collab_waiting_end"
  | "collab_close_begin"
  | "collab_close_end"
  | "collab_resume_begin"
  | "collab_resume_end"
  | "raw_response_item";

export type OrganizationAgentEventType =
  KnownOrganizationAgentEventType | (string & {});

export interface CadenceCollaborationModel {
  agents: CadenceAgent[];
  interactions: CadenceCollaborationInteraction[];
  delegatedAgents: CadenceDelegatedAgent[];
}

export function collaborationStateFromOrganization({
  status,
  liveWorker,
  statusError,
}: {
  status?: string;
  liveWorker: boolean;
  statusError?: string;
}): CollaborationAgentState {
  if (statusError || !liveWorker) return statusError ? "failed" : "idle";
  switch (status?.trim().toLowerCase()) {
    case "running":
      return "working";
    case "pending_init":
    case "waiting":
      return "waiting";
    case "completed":
    case "shutdown":
      return "completed";
    case "errored":
    case "not_found":
    case "interrupted":
      return "failed";
    default:
      return "idle";
  }
}

interface SessionEventLike {
  event_id?: string;
  session_id?: string;
  seq: number;
  ts_ms?: number;
  type: string;
  payload: UnknownRecord;
}

interface MutableInteraction extends CadenceCollaborationInteraction {
  targetAgentIds: string[];
}

interface RawFunctionLifecycle {
  agentId: string;
  callId: string;
  name: "call" | "wait";
  arguments: UnknownRecord;
  seq: number;
}

const PREVIEW_LIMIT = 240;
const PRIVATE_EVENT_TYPES = new Set<string>([
  "agent_reasoning_raw_content",
  "agent_reasoning_raw_content_delta",
  "reasoning_raw_content_delta",
  "debug_trace_snapshot",
  "incremental_compression_state",
  "coordinator_state",
  "agent_work_summary",
  "raw_response_item",
]);

const TOOL_LIFECYCLE_EVENT_TYPES = new Set<string>([
  "mcp_tool_call_begin",
  "mcp_tool_call_end",
  "web_search_begin",
  "web_search_end",
  "exec_command_begin",
  "exec_command_end",
]);
const RUNTIME_SUMMARY_PLACEHOLDERS = new Set<string>([
  "当前 Agent 未启动",
  "当前运行没有公开的 Agent 事件",
  "等待运行时摘要",
  "运行时状态不可用",
]);

const asRecord = (value: unknown): UnknownRecord | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : undefined;

const finiteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const normalizedText = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

const containsAbsolutePath = (value: string): boolean => {
  const withoutWebUrls = value.replace(/\bhttps?:\/\/\S+/gi, "");
  return (
    /(?:^|[^\p{L}\p{N}:])(?:~\/|\/(?!\/)|[a-z]:[\\/]|\\\\)\S+/iu.test(
      withoutWebUrls,
    ) || withoutWebUrls.toLowerCase().includes("file://")
  );
};

const containsUnsafePreview = (value: string): boolean =>
  containsAbsolutePath(value) ||
  /```|~~~/u.test(value) ||
  /(?:^|\n)\s*(?:\[[^\]\n]{2,80}\]|(?:context|request|goal|start point|boundaries|output contract|peer interface|system|developer)\s*:)/im.test(
    value,
  ) ||
  /<\s*\/?\s*(?:system|developer|assistant|tool|instructions?|context|request|goal)\b/i.test(
    value,
  ) ||
  /\b(?:system|developer)\s+(?:prompt|message|instruction(?:s)?)\b/i.test(
    value,
  ) ||
  /\b(?:model\s+pretraining|organization\s+messages?|system\s+messages?|developer\s+messages?)\b/i.test(
    value,
  ) ||
  /(?:ignore|override|disregard|forget).{0,40}(?:previous|prior|system|developer).{0,24}instruction(?:s)?/i.test(
    value,
  ) ||
  /chain[_ -]?of[_ -]?thought/i.test(value) ||
  /\b(?:encrypted(?:[_ -]?content)?|secret|token|password|passwd|authorization|bearer)\b/i.test(
    value,
  ) ||
  /\bapi[_ -]?key\b/i.test(value);

export function safeCollaborationPreview(value: unknown): string | undefined {
  const raw = text(value).replace(/\r/g, "");
  if (!raw || containsUnsafePreview(raw)) return undefined;
  const firstSentence =
    raw.match(/^([\s\S]*?(?:[。！？]|[.!?](?=\s|$)))/u)?.[1] ??
    raw.split("\n").find((line) => line.trim()) ??
    "";
  const preview = normalizedText(firstSentence);
  if (!preview) return undefined;
  return preview.length <= PREVIEW_LIMIT
    ? preview
    : `${preview.slice(0, PREVIEW_LIMIT - 1)}…`;
}

export function organizationAgentEventFromSse(
  event: SessionEventLike,
  activeProjectId: string,
  activeSessionId: string,
): OrganizationAgentEventRecord | undefined {
  if (event.type !== "organization.agent_event") return undefined;
  if (text(event.session_id) !== activeSessionId) return undefined;
  const payload = asRecord(event.payload);
  const projectId = text(payload?.project_id);
  const agentId = text(payload?.agent_id);
  const threadId = text(payload?.thread_id);
  const submissionId = text(payload?.submission_id);
  const rawEvent = asRecord(payload?.event);
  const eventType = text(rawEvent?.type);
  if (
    !rawEvent ||
    !projectId ||
    projectId !== activeProjectId ||
    !agentId ||
    !threadId ||
    !submissionId ||
    !eventType
  ) {
    return undefined;
  }
  const publicEvent = projectPublicEvent(rawEvent, eventType);
  if (!publicEvent) return undefined;
  const eventId = text(event.event_id) || undefined;
  return {
    key: organizationEventKey({
      projectId,
      agentId,
      threadId,
      submissionId,
      seq: event.seq,
      eventType,
      eventId,
      event: publicEvent,
    }),
    seq: event.seq,
    tsMs: event.ts_ms,
    eventId,
    projectId,
    agentId,
    threadId,
    submissionId,
    event: {
      ...publicEvent,
      type: eventType as OrganizationAgentEventType,
    },
  };
}

export function organizationAgentEventFromDurable(
  recordValue: unknown,
  activeProjectId: string,
  activeSessionId: string,
  fallbackIndex = 0,
): OrganizationAgentEventRecord | undefined {
  const record = asRecord(recordValue);
  if (!record) return undefined;
  const projectId = text(record.project_id ?? record.projectId);
  const sessionId = text(record.session_id ?? record.sessionId);
  const organizationSessionId = text(
    record.organization_session_id ?? record.organizationSessionId,
  );
  if (
    !projectId ||
    projectId !== activeProjectId ||
    (sessionId && activeSessionId && sessionId !== activeSessionId)
  ) {
    return undefined;
  }
  const rawEvent = asRecord(record.event);
  const eventType = text(rawEvent?.type);
  const agentId = text(record.agent_id ?? record.agentId);
  const threadId = text(record.thread_id ?? record.threadId);
  const source = text(record.source) || "organization.agent_rollout";
  const sourceIndex =
    finiteNumber(record.source_index ?? record.sourceIndex) ?? fallbackIndex;
  const sourceWatermark =
    typeof record.source_watermark === "string" ||
    typeof record.source_watermark === "number"
      ? record.source_watermark
      : typeof record.sourceWatermark === "string" ||
          typeof record.sourceWatermark === "number"
        ? record.sourceWatermark
        : undefined;
  const submissionId =
    text(record.submission_id ?? record.submissionId) ||
    text(rawEvent?.turn_id) ||
    `durable:${source}:${sourceWatermark ?? "unknown"}`;
  if (!rawEvent || !eventType || !agentId || !threadId) return undefined;
  const publicEvent = projectPublicEvent(rawEvent, eventType);
  if (!publicEvent) return undefined;
  return {
    key: organizationEventKey({
      projectId,
      agentId,
      threadId,
      submissionId,
      seq: sourceIndex,
      eventType,
      event: publicEvent,
    }),
    seq: sourceIndex,
    tsMs: finiteNumber(record.ts_ms ?? record.tsMs),
    source,
    sourceIndex,
    sourceWatermark,
    organizationSessionId: organizationSessionId || undefined,
    projectId,
    agentId,
    threadId,
    submissionId,
    event: {
      ...publicEvent,
      type: eventType as OrganizationAgentEventType,
    },
  };
}

export function organizationAgentEventsFromDurable(
  raw: UnknownRecord,
  activeProjectId: string,
  activeSessionId: string,
): OrganizationAgentEventRecord[] {
  const bundle = asRecord(raw.durable_agent_events);
  const records = Array.isArray(bundle?.records) ? bundle.records : [];
  return records.flatMap((record, index) => {
    const event = organizationAgentEventFromDurable(
      record,
      activeProjectId,
      activeSessionId,
      index + 1,
    );
    return event ? [event] : [];
  });
}

export function mergeOrganizationAgentEvents(
  current: OrganizationAgentEventRecord[],
  incoming: OrganizationAgentEventRecord | OrganizationAgentEventRecord[],
  limit?: number,
): OrganizationAgentEventRecord[] {
  const events = Array.isArray(incoming) ? incoming : [incoming];
  const deduped = new Map<string, OrganizationAgentEventRecord>();
  const insertionOrder = new Map<string, number>();
  let nextInsertionOrder = 0;
  [...current, ...events].forEach((event) => {
    const existing = deduped.get(event.key);
    if (existing) {
      deduped.set(event.key, preferredOrganizationEvent(existing, event));
      return;
    }
    deduped.set(event.key, event);
    insertionOrder.set(event.key, nextInsertionOrder++);
  });
  const ordered = [...deduped.values()].sort((left, right) => {
    const comparison = compareOrganizationAgentEvents(left, right);
    return (
      comparison ||
      (insertionOrder.get(left.key) ?? 0) - (insertionOrder.get(right.key) ?? 0)
    );
  });
  return limit === undefined ? ordered : ordered.slice(-Math.max(1, limit));
}

function preferredOrganizationEvent(
  existing: OrganizationAgentEventRecord,
  incoming: OrganizationAgentEventRecord,
): OrganizationAgentEventRecord {
  const existingIsLive = !existing.source;
  const incomingIsLive = !incoming.source;
  if (existingIsLive !== incomingIsLive) {
    return incomingIsLive ? incoming : existing;
  }
  if (existing.source !== incoming.source) return existing;

  const existingFieldCount = meaningfulEventFieldCount(existing.event);
  const incomingFieldCount = meaningfulEventFieldCount(incoming.event);
  const incomingIsRicher =
    incomingFieldCount > existingFieldCount ||
    (incomingFieldCount === existingFieldCount &&
      JSON.stringify(incoming.event).length >
        JSON.stringify(existing.event).length);
  return incomingIsRicher ? { ...existing, event: incoming.event } : existing;
}

function meaningfulEventFieldCount(event: UnknownRecord): number {
  return Object.entries(event).filter(
    ([key, value]) =>
      key !== "type" &&
      value !== undefined &&
      value !== null &&
      (typeof value !== "string" || Boolean(value.trim())),
  ).length;
}

function authoritativeRuntimeSummary(value: unknown): string {
  const summary = safeCollaborationPreview(value);
  return summary && !RUNTIME_SUMMARY_PLACEHOLDERS.has(summary)
    ? summary
    : "运行时摘要未公开";
}

export function compareOrganizationAgentEvents(
  left: Pick<
    OrganizationAgentEventRecord,
    "agentId" | "key" | "seq" | "source" | "sourceIndex" | "threadId"
  >,
  right: Pick<
    OrganizationAgentEventRecord,
    "agentId" | "key" | "seq" | "source" | "sourceIndex" | "threadId"
  >,
): number {
  const leftDurable = Boolean(left.source);
  const rightDurable = Boolean(right.source);
  if (leftDurable !== rightDurable) return leftDurable ? -1 : 1;
  if (!leftDurable)
    return left.seq - right.seq || left.key.localeCompare(right.key);

  const leftSourceRank = durableSourceRank(left.source);
  const rightSourceRank = durableSourceRank(right.source);
  if (leftSourceRank !== rightSourceRank) {
    return leftSourceRank - rightSourceRank;
  }
  const ownerComparison =
    left.agentId.localeCompare(right.agentId) ||
    left.threadId.localeCompare(right.threadId) ||
    text(left.source).localeCompare(text(right.source));
  if (ownerComparison) return ownerComparison;
  return (
    (left.sourceIndex ?? left.seq) - (right.sourceIndex ?? right.seq) ||
    left.key.localeCompare(right.key)
  );
}

function durableSourceRank(source?: string): number {
  if (source === "organization.agent_rollout") return 0;
  if (source === "organization.message_service") return 1;
  return 2;
}

function statusFromProtocol(value: unknown): AgentStatus | undefined {
  const direct = text(value).toLowerCase();
  if (direct) {
    if (direct === "running") return "working";
    if (direct === "pending_init") return "waiting";
    if (direct === "interrupted") return "paused";
    if (direct === "shutdown" || direct === "completed") return "complete";
    if (direct === "errored" || direct === "not_found") return "error";
  }
  const record = asRecord(value);
  if (!record) return undefined;
  if ("completed" in record) return "complete";
  if ("errored" in record) return "error";
  return undefined;
}

function collaborationStateFromProtocol(
  value: unknown,
): CollaborationAgentState | undefined {
  const direct = text(value).toLowerCase();
  if (direct) {
    if (direct === "running") return "working";
    if (direct === "pending_init" || direct === "waiting") return "waiting";
    if (direct === "shutdown" || direct === "completed") return "completed";
    if (
      direct === "errored" ||
      direct === "not_found" ||
      direct === "interrupted"
    ) {
      return "failed";
    }
  }
  const record = asRecord(value);
  if (!record) return undefined;
  if ("completed" in record) return "completed";
  if ("errored" in record) return "failed";
  return undefined;
}

function eventThreadId(event: UnknownRecord, key: string): string {
  return text(event[key]);
}

function callbackPreview(targets: unknown): string | undefined {
  if (!Array.isArray(targets)) return undefined;
  return targets
    .map((target) =>
      safeCollaborationPreview(asRecord(target)?.callback_content),
    )
    .find((preview): preview is string => Boolean(preview));
}

function waitTargetThreadIds(targets: unknown): string[] {
  if (!Array.isArray(targets)) return [];
  return targets
    .map((target) =>
      eventThreadId(asRecord(target) ?? {}, "receiver_thread_id"),
    )
    .filter(Boolean);
}

function jsonRecord(value: unknown): UnknownRecord | undefined {
  if (typeof value !== "string") return asRecord(value);
  try {
    return asRecord(JSON.parse(value));
  } catch {
    return undefined;
  }
}

const PUBLIC_PLAN_ITEM_STATUSES = new Set([
  "pending",
  "in_progress",
  "completed",
]);

function canonicalPlan(value: unknown): UnknownRecord[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value.flatMap((item) => {
    const record = asRecord(item);
    const step = text(record?.step);
    const status = text(record?.status).toLowerCase();
    return step && PUBLIC_PLAN_ITEM_STATUSES.has(status)
      ? [{ step, status }]
      : [];
  });
  return items.length === value.length ? items : undefined;
}

function projectPublicEvent(
  rawEvent: UnknownRecord,
  eventType: string,
): UnknownRecord | undefined {
  if (PRIVATE_EVENT_TYPES.has(eventType)) return undefined;
  if (TOOL_LIFECYCLE_EVENT_TYPES.has(eventType) && !text(rawEvent.call_id)) {
    return undefined;
  }
  if (eventType === "plan_update") {
    const plan = canonicalPlan(rawEvent.plan);
    return plan ? { ...rawEvent, plan } : { ...rawEvent, plan: [] };
  }
  if (
    eventType === "collab_agent_spawn_begin" ||
    eventType === "collab_agent_spawn_end" ||
    eventType === "collab_agent_interaction_begin" ||
    eventType === "collab_agent_interaction_end"
  ) {
    return { ...rawEvent, prompt: "[redacted]" };
  }
  if (eventType !== "plan_delta") return rawEvent;

  const structuredPlan = canonicalPlan(rawEvent.plan ?? rawEvent.items);
  if (structuredPlan) {
    return { ...rawEvent, plan: structuredPlan, delta: undefined };
  }
  const parsedDelta = jsonRecord(rawEvent.delta);
  const parsedPlan = canonicalPlan(parsedDelta?.plan);
  if (parsedPlan) {
    return { ...rawEvent, plan: parsedPlan, delta: undefined };
  }
  return Object.fromEntries(
    Object.entries(rawEvent).filter(([key]) => key !== "delta"),
  );
}

function organizationEventKey({
  projectId,
  agentId,
  threadId,
  submissionId,
  seq,
  eventType,
  eventId,
  event,
}: {
  projectId: string;
  agentId: string;
  threadId: string;
  submissionId: string;
  seq: number;
  eventType: string;
  eventId?: string;
  event: UnknownRecord;
}): string {
  const callId = text(event.call_id);
  if (callId && TOOL_LIFECYCLE_EVENT_TYPES.has(eventType)) {
    return `${projectId}:${agentId}:${threadId}:${submissionId}:${eventType}:${callId}`;
  }
  const turnId = text(event.turn_id);
  if (
    turnId &&
    ["turn_started", "turn_complete", "turn_aborted"].includes(eventType)
  ) {
    return `${projectId}:${agentId}:${threadId}:${eventType}:turn:${turnId}`;
  }
  if (eventType === "agent_message") {
    const message = text(event.message);
    const summary = text(event.summary);
    if (message || summary) {
      return `${projectId}:${agentId}:${threadId}:${eventType}:${turnId}:${message}:${summary}`;
    }
  }
  if (eventType === "plan_update" && Array.isArray(event.plan)) {
    return `${projectId}:${agentId}:${threadId}:${eventType}:${turnId}:${JSON.stringify(event.plan)}`;
  }
  if (eventType === "raw_response_item") {
    const item = asRecord(event.item);
    const rawCallId = text(item?.call_id);
    const itemType = text(item?.type);
    if (rawCallId && itemType) {
      return `${projectId}:${agentId}:${threadId}:${eventType}:${itemType}:${rawCallId}`;
    }
  }
  return (
    eventId ??
    `${projectId}:${seq}:${agentId}:${threadId}:${submissionId}:${eventType}`
  );
}

function rawResponseItem(event: UnknownRecord): UnknownRecord | undefined {
  return asRecord(event.item);
}

function rawOutputRecord(item: UnknownRecord): UnknownRecord | undefined {
  const direct = jsonRecord(item.output);
  if (direct) return direct;
  if (!Array.isArray(item.output)) return undefined;
  for (const contentItem of item.output) {
    const record = asRecord(contentItem);
    if (!record) continue;
    if (
      "message" in record ||
      "pendingReplyTargets" in record ||
      "pending_reply_targets" in record ||
      "timedOut" in record ||
      "timed_out" in record
    ) {
      return record;
    }
    const nested = jsonRecord(record.text ?? record.content ?? record.output);
    if (nested) return nested;
  }
  return undefined;
}

function stringList(values: Array<unknown>): string[] {
  return values.map(text).filter(Boolean);
}

function delegatedAgentKey(
  projectId: string,
  parentThreadId: string,
  childThreadId: string,
): string {
  return `${projectId}:provider-subagent:${parentThreadId}:${childThreadId}`;
}

function delegatedStatusFromWaitState(value: unknown): AgentStatus | undefined {
  switch (text(value).toLowerCase()) {
    case "pending":
    case "pending_init":
      return "waiting";
    case "running":
      return "working";
    case "completed":
      return "complete";
    case "failed":
    case "errored":
      return "error";
    case "timed_out":
      return "paused";
    default:
      return undefined;
  }
}

/**
 * Derives the PASEO-style provider-child list from public VCode protocol facts.
 *
 * `OrganizationAgentEventRecord` deliberately stays a transport envelope:
 * `record.threadId` says who emitted an event. A child exists only after a
 * `collab_agent_spawn_end.new_thread_id` route, so a receiver of an ordinary
 * send/wait call can update an existing child but can never manufacture one.
 */
export function buildCadenceDelegatedAgents({
  agents,
  events,
  projectId,
}: {
  agents: CadenceAgent[];
  events: OrganizationAgentEventRecord[];
  projectId: string;
}): CadenceDelegatedAgent[] {
  if (!projectId) return [];
  const scopedEvents = mergeOrganizationAgentEvents(
    [],
    events.filter((event) => event.projectId === projectId),
  );
  const rosterByThreadId = new Map(
    agents
      .filter((agent) => Boolean(agent.threadId))
      .map((agent) => [agent.threadId as string, agent]),
  );
  const delegatedByKey = new Map<string, CadenceDelegatedAgent>();
  const delegatedByThreadId = new Map<string, CadenceDelegatedAgent[]>();

  const register = (
    parentThreadId: string,
    childThreadId: string,
    record: OrganizationAgentEventRecord,
    order: number,
  ) => {
    if (!parentThreadId || !childThreadId || parentThreadId === childThreadId) {
      return;
    }
    const key = delegatedAgentKey(projectId, parentThreadId, childThreadId);
    const existing = delegatedByKey.get(key);
    const rosterAgent = rosterByThreadId.get(childThreadId);
    const reportedSpawnStatus = statusFromProtocol(record.event.status);
    // `spawn_end` proves the route exists, but its terminal-looking tool
    // result is not a child-turn completion. Only child lifecycle/history may
    // settle a row as complete, so a completed spawn snapshot stays active.
    const status =
      reportedSpawnStatus === "complete" ? "working" : reportedSpawnStatus ?? "working";
    if (existing) {
      if (order >= existing.updatedSeq) {
        existing.status = status;
        existing.updatedSeq = order;
      }
      return;
    }
    const delegated: CadenceDelegatedAgent = {
      id: key,
      projectId,
      parentThreadId,
      childThreadId,
      parentCallId: text(record.event.call_id) || undefined,
      rosterAgentId: rosterAgent?.id,
      // Spawn prompts are intentionally redacted by the Project event seam.
      // Until VCode exposes a public task descriptor, do not reconstruct one
      // from parent summaries or tool arguments.
      title: rosterAgent?.name ?? "运行时协作者",
      subtitle: rosterAgent?.role ?? "VCode 子智能体",
      status,
      createdSeq: order,
      updatedSeq: order,
    };
    delegatedByKey.set(key, delegated);
    const byThread = delegatedByThreadId.get(childThreadId) ?? [];
    byThread.push(delegated);
    delegatedByThreadId.set(childThreadId, byThread);
  };

  // First discover every stable child route. This second-pass shape is the
  // bounded replay equivalent for the in-memory Project event list: a child
  // event that arrived before its parent spawn cannot be misprojected as root.
  scopedEvents.forEach((record, order) => {
    if (record.event.type !== "collab_agent_spawn_end") return;
    const parentThreadId =
      eventThreadId(record.event, "sender_thread_id") || record.threadId;
    register(
      parentThreadId,
      eventThreadId(record.event, "new_thread_id"),
      record,
      order,
    );
  });

  const updateByThreadId = (
    childThreadId: string,
    status: AgentStatus | undefined,
    order: number,
  ) => {
    if (!childThreadId || !status) return;
    delegatedByThreadId.get(childThreadId)?.forEach((delegated) => {
      if (order < delegated.updatedSeq) return;
      delegated.status = status;
      delegated.updatedSeq = order;
    });
  };

  scopedEvents.forEach((record, order) => {
    const event = record.event;
    const ownThreadChildren = delegatedByThreadId.get(record.threadId);
    if (ownThreadChildren?.length) {
      const ownStatus =
        event.type === "task_started" || event.type === "turn_started"
          ? "working"
          : event.type === "task_complete" || event.type === "turn_complete"
            ? "complete"
            : event.type === "turn_aborted"
              ? "paused"
              : event.type === "error" || event.type === "stream_error"
                ? "error"
                : undefined;
      updateByThreadId(record.threadId, ownStatus, order);
    }

    if (
      event.type === "collab_agent_interaction_end" ||
      event.type === "collab_waiting_end" ||
      event.type === "collab_resume_end"
    ) {
      const receiverThreadId = eventThreadId(event, "receiver_thread_id");
      updateByThreadId(receiverThreadId, statusFromProtocol(event.status), order);
    }

    // VCode's close event reports the status observed *before* the close. It
    // is route provenance, not a child lifecycle result, so treating it as a
    // cancel/completion would manufacture a Runtime fact PASEO does not infer.

    if (event.type === "collab_waiting_end" && Array.isArray(event.targets)) {
      event.targets.forEach((target) => {
        const targetRecord = asRecord(target);
        updateByThreadId(
          eventThreadId(targetRecord ?? {}, "receiver_thread_id"),
          delegatedStatusFromWaitState(targetRecord?.state),
          order,
        );
      });
    }
  });

  return [...delegatedByKey.values()].sort(
    (left, right) =>
      left.createdSeq - right.createdSeq || left.id.localeCompare(right.id),
  );
}

export function buildCadenceCollaborationModel({
  agents,
  events,
  projectId,
}: {
  agents: CadenceAgent[];
  events: OrganizationAgentEventRecord[];
  projectId: string;
}): CadenceCollaborationModel {
  const scopedEvents = mergeOrganizationAgentEvents(
    [],
    events.filter((event) => event.projectId === projectId),
  );
  const agentById = new Map(
    agents.map((agent) => [
      agent.id,
      { ...agent, summary: authoritativeRuntimeSummary(agent.summary) },
    ]),
  );
  const authoritativeStatusByAgentId = new Map(
    agents.map((agent) => [agent.id, agent.status]),
  );
  const threadToAgentId = new Map<string, string>();

  agents.forEach((agent) => {
    if (agent.threadId) threadToAgentId.set(agent.threadId, agent.id);
  });
  scopedEvents.forEach((record) => {
    if (agentById.has(record.agentId)) {
      threadToAgentId.set(record.threadId, record.agentId);
    }
  });

  const statusSeq = new Map<string, number>();
  const collaborationStateSeq = new Map<string, number>();
  const summarySeq = new Map<string, number>();
  const interactions = new Map<string, MutableInteraction>();
  const rawCalls = new Map<string, RawFunctionLifecycle>();

  const resolveAgentId = (
    threadId: string,
    fallbackAgentId?: string,
  ): string | undefined => {
    if (threadId && threadToAgentId.has(threadId))
      return threadToAgentId.get(threadId);
    return fallbackAgentId && agentById.has(fallbackAgentId)
      ? fallbackAgentId
      : undefined;
  };

  const updateAgent = (
    agentId: string | undefined,
    seq: number,
    status?: AgentStatus,
    summary?: string,
    threadId?: string,
    collaborationState?: CollaborationAgentState,
    source?: string,
  ) => {
    if (!agentId) return;
    const agent = agentById.get(agentId);
    if (!agent) return;
    const authoritativeStatus = authoritativeStatusByAgentId.get(agentId);
    const contradictsAuthoritativeStatus =
      Boolean(source) &&
      Boolean(status) &&
      authoritativeStatus !== undefined &&
      authoritativeStatus !== "unknown" &&
      status !== authoritativeStatus;
    if (contradictsAuthoritativeStatus) {
      status = undefined;
      summary = undefined;
      collaborationState = undefined;
    }
    if (threadId && !agent.threadId) agent.threadId = threadId;
    if (status && seq >= (statusSeq.get(agentId) ?? -1)) {
      agent.status = status;
      statusSeq.set(agentId, seq);
    }
    if (summary && seq >= (summarySeq.get(agentId) ?? -1)) {
      agent.summary = summary;
      summarySeq.set(agentId, seq);
    }
    if (
      collaborationState &&
      seq >= (collaborationStateSeq.get(agentId) ?? -1)
    ) {
      agent.collaborationState = collaborationState;
      collaborationStateSeq.set(agentId, seq);
    }
  };

  scopedEvents.forEach((record, projectionOrder) => {
    const event = record.event;
    const ownerAgentId = resolveAgentId(record.threadId, record.agentId);
    const type = event.type;

    if (type === "task_started" || type === "turn_started") {
      updateAgent(
        ownerAgentId,
        projectionOrder,
        "working",
        "已开始处理任务",
        record.threadId,
        "working",
        record.source,
      );
    } else if (type === "task_complete" || type === "turn_complete") {
      updateAgent(
        ownerAgentId,
        projectionOrder,
        "complete",
        safeCollaborationPreview(event.last_agent_message) ?? "任务已完成",
        record.threadId,
        "completed",
        record.source,
      );
    } else if (type === "turn_aborted") {
      updateAgent(
        ownerAgentId,
        projectionOrder,
        "paused",
        "任务已中止",
        record.threadId,
        "failed",
        record.source,
      );
    } else if (type === "error") {
      updateAgent(
        ownerAgentId,
        projectionOrder,
        "error",
        safeCollaborationPreview(event.message) ?? "运行发生异常",
        record.threadId,
        "failed",
        record.source,
      );
    } else if (type === "collab_agent_spawn_begin") {
      updateAgent(
        ownerAgentId,
        projectionOrder,
        "working",
        "正在启动协作者",
        record.threadId,
        "working",
        record.source,
      );
    } else if (type === "collab_agent_spawn_end") {
      const spawnedThreadId = eventThreadId(event, "new_thread_id");
      const spawnedAgentId = resolveAgentId(spawnedThreadId);
      const spawnedStatus = statusFromProtocol(event.status);
      const spawnedCollaborationState = collaborationStateFromProtocol(
        event.status,
      );
      updateAgent(
        ownerAgentId,
        projectionOrder,
        "working",
        "已启动协作者",
        record.threadId,
        "working",
        record.source,
      );
      updateAgent(
        spawnedAgentId,
        projectionOrder,
        spawnedStatus,
        undefined,
        spawnedThreadId || undefined,
        spawnedCollaborationState,
        record.source,
      );
    } else if (
      type === "collab_agent_interaction_begin" ||
      type === "collab_agent_interaction_end"
    ) {
      const senderThreadId = eventThreadId(event, "sender_thread_id");
      const receiverThreadId = eventThreadId(event, "receiver_thread_id");
      const sourceAgentId = resolveAgentId(senderThreadId, ownerAgentId);
      const targetAgentId = resolveAgentId(receiverThreadId);
      const preview = safeCollaborationPreview(event.prompt);
      const targetName = targetAgentId
        ? agentById.get(targetAgentId)?.name
        : undefined;
      updateAgent(
        sourceAgentId,
        projectionOrder,
        "working",
        preview
          ? `${targetName ?? "协作者"}：${preview}`
          : targetName
            ? `正在与 ${targetName} 协作`
            : "正在进行协作",
        senderThreadId || record.threadId,
        "communicating",
        record.source,
      );
      if (!sourceAgentId || !targetAgentId || sourceAgentId === targetAgentId)
        return;
      const callId = text(event.call_id) || record.submissionId;
      const interactionId = `${projectId}:interaction:${callId}:${senderThreadId}:${receiverThreadId}`;
      const current = interactions.get(interactionId);
      interactions.set(interactionId, {
        id: interactionId,
        kind: "interaction",
        protocolSource: "native",
        sourceAgentId,
        targetAgentIds: [targetAgentId],
        preview: current?.preview ?? preview,
        seq: current?.seq ?? record.seq,
        updatedSeq: record.seq,
        completed:
          current?.completed || type === "collab_agent_interaction_end",
      });
    } else if (
      type === "collab_waiting_begin" ||
      type === "collab_waiting_end"
    ) {
      const senderThreadId = eventThreadId(event, "sender_thread_id");
      const sourceAgentId = resolveAgentId(senderThreadId, ownerAgentId);
      const targetAgentIds = waitTargetThreadIds(event.targets)
        .map((threadId) => resolveAgentId(threadId))
        .filter((agentId): agentId is string =>
          Boolean(agentId && agentId !== sourceAgentId),
        );
      const uniqueTargetAgentIds = [...new Set(targetAgentIds)];
      const targetNames = uniqueTargetAgentIds
        .map((agentId) => agentById.get(agentId)?.name)
        .filter((name): name is string => Boolean(name));
      const timedOut = event.timed_out === true;
      const collaborationState =
        type === "collab_waiting_begin"
          ? "waiting"
          : timedOut
            ? "failed"
            : uniqueTargetAgentIds.length
              ? "communicating"
              : "working";
      updateAgent(
        sourceAgentId,
        projectionOrder,
        type === "collab_waiting_begin" ? "waiting" : "working",
        type === "collab_waiting_begin"
          ? targetNames.length
            ? `正在等待 ${targetNames.join("、")}`
            : "正在等待协作者"
          : timedOut
            ? "等待协作者超时"
            : "已收到协作者反馈",
        senderThreadId || record.threadId,
        collaborationState,
        record.source,
      );
      if (!sourceAgentId || uniqueTargetAgentIds.length === 0) return;
      const callId = text(event.call_id) || record.submissionId;
      const interactionId = `${projectId}:waiting:${callId}:${senderThreadId}`;
      const current = interactions.get(interactionId);
      interactions.set(interactionId, {
        id: interactionId,
        kind: "waiting",
        protocolSource: "native",
        sourceAgentId,
        targetAgentIds: [
          ...new Set([
            ...(current?.targetAgentIds ?? []),
            ...uniqueTargetAgentIds,
          ]),
        ],
        preview: current?.preview ?? callbackPreview(event.targets),
        seq: current?.seq ?? record.seq,
        updatedSeq: record.seq,
        completed: current?.completed || type === "collab_waiting_end",
      });
    } else if (type === "raw_response_item") {
      const item = rawResponseItem(event);
      const itemType = text(item?.type);
      const callId = text(item?.call_id);
      const lifecycleKey = `${record.agentId}:${callId}`;
      if (!item || !callId) return;

      if (itemType === "function_call") {
        const name = text(item.name);
        if (name !== "call" && name !== "wait") return;
        const argumentsRecord = jsonRecord(item.arguments);
        if (!argumentsRecord) return;
        rawCalls.set(lifecycleKey, {
          agentId: record.agentId,
          callId,
          name,
          arguments: argumentsRecord,
          seq: record.seq,
        });
        if (name === "wait") {
          updateAgent(
            ownerAgentId,
            projectionOrder,
            "waiting",
            "正在等待协作者",
            record.threadId,
            "waiting",
            record.source,
          );
          const targetAgentId = text(argumentsRecord.target_agent_id);
          if (
            ownerAgentId &&
            targetAgentId &&
            agentById.has(targetAgentId) &&
            targetAgentId !== ownerAgentId
          ) {
            const interactionId = `${projectId}:raw:wait:${record.agentId}:${callId}`;
            interactions.set(interactionId, {
              id: interactionId,
              kind: "waiting",
              protocolSource: "raw_response_item",
              sourceAgentId: ownerAgentId,
              targetAgentIds: [targetAgentId],
              seq: record.seq,
              updatedSeq: record.seq,
              completed: false,
            });
          }
          return;
        }

        const targetAgentId = text(
          argumentsRecord.target_agent_name ?? argumentsRecord.agent_id,
        );
        const contentPreview = safeCollaborationPreview(
          argumentsRecord.content,
        );
        const messageId = text(argumentsRecord.message_id);
        const needReply = argumentsRecord.need_reply === true;
        const targetAgent = agentById.get(targetAgentId);
        updateAgent(
          ownerAgentId,
          projectionOrder,
          "working",
          contentPreview
            ? `${targetAgent?.name ?? targetAgentId}：${contentPreview}`
            : targetAgent
              ? `正在与 ${targetAgent.name} 协作`
              : "正在进行协作",
          record.threadId,
          "communicating",
          record.source,
        );
        if (
          !ownerAgentId ||
          !targetAgent ||
          ownerAgentId === targetAgentId ||
          !messageId
        )
          return;
        const interactionId = `${projectId}:raw:call:${record.agentId}:${callId}`;
        interactions.set(interactionId, {
          id: interactionId,
          kind: "interaction",
          protocolSource: "raw_response_item",
          sourceAgentId: ownerAgentId,
          targetAgentIds: [targetAgentId],
          preview: contentPreview,
          messageId,
          needReply,
          seq: record.seq,
          updatedSeq: record.seq,
          completed: false,
        });
        return;
      }

      if (itemType !== "function_call_output") return;
      const lifecycle = rawCalls.get(lifecycleKey);
      if (!lifecycle) return;
      if (lifecycle.name === "call") {
        const interactionId = `${projectId}:raw:call:${record.agentId}:${callId}`;
        const current = interactions.get(interactionId);
        if (current) {
          interactions.set(interactionId, {
            ...current,
            updatedSeq: record.seq,
            completed: true,
          });
        }
        return;
      }

      const output = rawOutputRecord(item);
      if (!output) return;
      const message = asRecord(output.message);
      const pendingTargets = Array.isArray(output.pendingReplyTargets)
        ? output.pendingReplyTargets
        : Array.isArray(output.pending_reply_targets)
          ? output.pending_reply_targets
          : [];
      const messageSender = text(message?.sender);
      const pendingAgentIds = pendingTargets.flatMap((target) => {
        const targetRecord = asRecord(target);
        return stringList([
          targetRecord?.participantId,
          targetRecord?.participant_id,
        ]).slice(0, 1);
      });
      const targetAgentIds = [
        ...new Set([messageSender, ...pendingAgentIds]),
      ].filter((agentId) => agentById.has(agentId) && agentId !== ownerAgentId);
      const messagePreview = safeCollaborationPreview(message?.content);
      const messageId = text(message?.messageId ?? message?.message_id);
      const needReply =
        message?.needReply === true || message?.need_reply === true;
      updateAgent(
        ownerAgentId,
        projectionOrder,
        "working",
        output.timedOut === true || output.timed_out === true
          ? "等待协作者超时"
          : messagePreview
            ? `已收到协作者反馈：${messagePreview}`
            : "已结束等待",
        record.threadId,
        targetAgentIds.length ? "communicating" : "waiting",
        record.source,
      );
      const interactionId = `${projectId}:raw:wait:${record.agentId}:${callId}`;
      const current = interactions.get(interactionId);
      const resolvedTargetAgentIds = [
        ...new Set([...(current?.targetAgentIds ?? []), ...targetAgentIds]),
      ];
      if (!ownerAgentId || resolvedTargetAgentIds.length === 0) return;
      interactions.set(interactionId, {
        id: interactionId,
        kind: "waiting",
        protocolSource: "raw_response_item",
        sourceAgentId: ownerAgentId,
        targetAgentIds: resolvedTargetAgentIds,
        preview: current?.preview ?? messagePreview,
        messageId: current?.messageId ?? (messageId || undefined),
        needReply: current?.needReply ?? needReply,
        seq: current?.seq ?? lifecycle.seq,
        updatedSeq: record.seq,
        completed: true,
      });
    }
  });

  return {
    agents: agents.map((agent) => agentById.get(agent.id) ?? agent),
    delegatedAgents: buildCadenceDelegatedAgents({
      agents,
      events: scopedEvents,
      projectId,
    }),
    interactions: [...interactions.values()]
      .filter(
        (interaction) =>
          agentById.has(interaction.sourceAgentId) &&
          interaction.targetAgentIds.every((agentId) => agentById.has(agentId)),
      )
      .sort(
        (left, right) =>
          left.seq - right.seq ||
          left.updatedSeq - right.updatedSeq ||
          left.id.localeCompare(right.id),
      ),
  };
}
